/**
 * Janela intradiária ("agora até às 18 horas", "nas próximas 3 horas", "hoje").
 *
 * Módulo puro e multilingue (pt/en/es/fr/it/de): sem IA, sem Maps, sem custos.
 * Serve para impedir que um pedido do próprio dia seja lido como uma viagem de
 * vários dias — e para que "até às 18 horas" nunca vire "18 horas" de duração.
 */

import { parseRelativeDayWindow } from "./relative-day-window";

export type IntradayWindow = {
  /** O pedido é claramente do próprio dia. */
  intraday: true;
  /** Fim do dia pedido, em minutos desde a meia-noite (quando indicado). */
  endMinutes?: number;
  /** Duração calculada em horas (quando é possível calcular). */
  durationHours?: number;
  /** A hora final indicada já passou. */
  expired: boolean;
};

/** Pergunta curta (grátis) quando a hora final indicada já passou.
 *  Evita assumir "hoje" — o utilizador pode ter pedido amanhã ou outro dia.
 */
const EXPIRED_QUESTION: Record<string, string> = {
  pt: "Essa hora já passou. Até que horas queres explorar?",
  en: "That time has already passed. Until what time do you want to explore?",
  es: "Esa hora ya pasó. ¿Hasta qué hora quieres explorar?",
  fr: "Cette heure est déjà passée. Jusqu'à quelle heure veux-tu explorer ?",
  it: "Quell'ora è già passata. Fino a che ora vuoi esplorare?",
  de: "Diese Uhrzeit ist schon vorbei. Bis wann möchtest du unterwegs sein?",
};

export function expiredWindowQuestion(language = "pt"): string {
  const key = (language || "pt").slice(0, 2).toLowerCase();
  return EXPIRED_QUESTION[key] ?? EXPIRED_QUESTION.en;
}

/**
 * Mantém a assinatura antiga mas delega na deteção de dia relativo. O fuso
 * pode ser passado para que "amanhã" seja interpretado corretamente no país do
 * destino; sem fuso, usa o do dispositivo.
 */
export function parseIntradayWindow(text: string, now?: Date, timeZone?: string): IntradayWindow | null {
  const window = parseRelativeDayWindow(text, timeZone, now);
  if (!window) return null;
  return {
    intraday: true,
    endMinutes: window.endMinutes,
    durationHours: window.durationHours,
    expired: window.expired,
  };
}
